import React, { useEffect, useRef } from "react";
import { WalkthroughStep } from "./types";
import { FONT_BODY, FONT_HEAD } from "../../src/constants/theme";

interface Props {
  step: WalkthroughStep;
  stepNumber: number;
  totalSteps: number;
  position: React.CSSProperties;
  onNext: () => void;
  onSkip: () => void;
  selectionMessage?: string | null;
}

export function WalkthroughTooltip({
  step,
  stepNumber,
  totalSteps,
  position,
  onNext,
  onSkip,
  selectionMessage,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const isLast = stepNumber === totalSteps;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll<HTMLElement>("button");
    focusable[focusable.length - 1]?.focus();

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener("keydown", trapFocus);
    return () => dialog.removeEventListener("keydown", trapFocus);
  }, [step.id]);

  return (
    <div
      ref={dialogRef}
      className="ek-walkthrough-tooltip"
      data-placement={step.placement || "bottom"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ek-walkthrough-title"
      aria-describedby="ek-walkthrough-description"
      style={{ ...position, fontFamily: FONT_BODY }}
    >
      <span className="ek-walkthrough-pointer" aria-hidden="true" />
      <div className="ek-walkthrough-card-header">
        <span aria-label={`Step ${stepNumber} of ${totalSteps}`}>
          {stepNumber}/{totalSteps}
        </span>
        <button
          type="button"
          className="ek-walkthrough-close"
          onClick={onSkip}
          aria-label="Close and skip product tour"
        >
          ×
        </button>
      </div>
      <div className="ek-walkthrough-card-body">
        <div
          className="ek-walkthrough-progress"
          aria-label={`Tour progress: step ${stepNumber} of ${totalSteps}`}
        >
          <div>
            <i style={{ width: `${(stepNumber / totalSteps) * 100}%` }} />
          </div>
        </div>
        <h2 id="ek-walkthrough-title" style={{ fontFamily: FONT_HEAD }}>
          {step.title}
        </h2>
        <p id="ek-walkthrough-description">{step.description}</p>
        {step.requiresSelection && (
          <div className="ek-walkthrough-select-hint">
            <span>Select one highlighted item to continue.</span>
          </div>
        )}
        {selectionMessage && (
          <div className="ek-walkthrough-validation" role="alert">
            {selectionMessage}
          </div>
        )}
        <div className="ek-walkthrough-actions">
          <button
            className="ek-walkthrough-skip"
            onClick={onSkip}
            aria-label="Skip product tour"
          >
            Skip tour
          </button>
          <div>
            <button className="ek-walkthrough-primary" onClick={onNext}>
              {isLast ? "Start Learning" : "Next"}
              {!isLast && <span aria-hidden="true"> →</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
